/** Bio-Calibration Phase — profile extensions and session metrics ledger. */

export type TrainingGoal = 'strength' | 'hypertrophy' | 'conditioning_weight_loss'

export type TrainingAge = 'beginner' | 'intermediate' | 'advanced'

export type CalibrationStatus = 'not_started' | 'in_progress' | 'completed'

export type CalibrationSessionType =
  | 'upper_compound'
  | 'lower_compound'
  | 'cardio_efficiency'
  | 'isolation_density'

/** Canonical skeletal nodes referenced by the injury inventory map. */
export type SkeletalNode =
  | 'left_shoulder'
  | 'right_shoulder'
  | 'left_elbow'
  | 'right_elbow'
  | 'left_wrist'
  | 'right_wrist'
  | 'left_hip'
  | 'right_hip'
  | 'left_knee'
  | 'right_knee'
  | 'left_ankle'
  | 'right_ankle'
  | 'lower_back'
  | 'upper_back'
  | 'neck'
  | 'chest'
  | 'abdomen'

export type InjurySeverity = 'mild' | 'moderate' | 'severe' | 'acute'

/** Per-node injury record stored under orthopedic_injury_inventory. */
export type OrthopedicInjuryEntry = {
  severity: InjurySeverity
  tags?: string[]
  notes?: string
  /** ISO date when the issue was first logged. */
  since?: string
}

/**
 * Structured injury map keyed by skeletal node.
 * Replaces the legacy free-text `medical_niggles` profile field.
 */
export type OrthopedicInjuryInventory = Partial<Record<SkeletalNode, OrthopedicInjuryEntry>>

/** Sub-second tempo deviation captured per rep during calibration sets. */
export type VelocityDeviationEntry = {
  rep_index: number
  /** Signed delta from target tempo phase timing (milliseconds). */
  delta_ms: number
  phase?: 'eccentric' | 'pause' | 'concentric' | 'lockout'
}

/** BPM drop from session peak HR at fixed post-set checkpoints. */
export type HeartRateRecoveryDelta = {
  bpm_drop_60s?: number | null
  bpm_drop_120s?: number | null
  peak_hr_bpm?: number | null
}

/** Profile columns introduced by the Bio-Calibration migration. */
export type BioCalibrationProfileFields = {
  dob: string | null
  training_goal: TrainingGoal | null
  training_age: TrainingAge | null
  orthopedic_injury_inventory: OrthopedicInjuryInventory
  calibration_status: CalibrationStatus
  /** 0–4; at 4 the system transitions out of calibration. */
  calibration_completed_sessions: number
}

export type CalibrationMetricsRow = {
  id: string
  user_id: string
  session_type: CalibrationSessionType
  /** Percentage of reps matching the 3-0-1-0 tempo prescription. */
  tempo_compliance_rate: number | null
  velocity_deviation_history: VelocityDeviationEntry[]
  /** Rep-percentage drop-off across sets under strict rest intervals. */
  work_density_dropoff: number | null
  heart_rate_recovery_delta: HeartRateRecoveryDelta
  /** Average pace-to-HR efficiency score during cardio threshold tests. */
  cardio_pace_hr_ratio: number | null
  mobility_flags: string[]
  created_at: string
}

export type CalibrationMetricsInsert = Omit<CalibrationMetricsRow, 'id' | 'created_at'> & {
  id?: string
  created_at?: string
}

/** Returns true once the production engine has fully activated (not merely session 4 logged). */
export function isCalibrationComplete(
  profile: Partial<Pick<BioCalibrationProfileFields, 'calibration_status' | 'calibration_completed_sessions'>>,
): boolean {
  return profile.calibration_status === 'completed'
}

/** Maps legacy free-text niggles into a single-node inventory entry (best-effort). */
export function nigglesTextToInventory(text: string, node: SkeletalNode = 'lower_back'): OrthopedicInjuryInventory {
  const trimmed = text.trim()
  if (!trimmed) return {}
  return {
    [node]: {
      severity: 'mild',
      notes: trimmed,
      tags: ['legacy_niggles_import'],
    },
  }
}
